<template>
  <BContainer class="ratings-page">
    <!-- zaglavlje: ko je ocenjen, statistika, akcije -->
    <section class="rt-card rt-head">
      <div class="rt-head-top">
        <span class="rt-av" aria-hidden="true">{{ initials(customer?.customer_name) }}</span>

        <div>
          <h1 class="rt-title h1-title">{{ pageTitle }}</h1>

          <div v-if="customer?.customer_created_at" class="rt-meta">
            Član od: {{ formatDate(customer?.customer_created_at) }}
          </div>
        </div>
      </div>

      <template v-if="totalRatings > 0">
        <div class="rt-stats">
          <div>
            <div class="rt-stat-num pos">{{ positiveCount }}</div>
            <div class="rt-stat-lab">Pozitivnih</div>
          </div>
          <div>
            <div class="rt-stat-num neg">{{ negativeCount }}</div>
            <div class="rt-stat-lab">Negativnih</div>
          </div>
          <div>
            <div class="rt-stat-num">{{ positivePercent }}%</div>
            <div class="rt-stat-lab">Pozitivnih ocena</div>
          </div>
        </div>

        <div
          class="rt-bar"
          role="img"
          :aria-label="`${positivePercent}% pozitivnih ocena`"
        >
          <i :style="{ width: positivePercent + '%' }"></i>
        </div>
      </template>

      <p v-else class="rt-none">Još nema ocena.</p>

      <div class="rt-head-actions">
        <NuxtLink
          v-if="customer.id"
          :to="{name: 'oglas-svi-oglasi-customer-id',
               params:{customer: $srbTolat.srbTolat(customer.customer_name),
                                                    id: customer.id}}"
          class="rt-btn"
        >
          Svi oglasi
        </NuxtLink>

        <button
          v-if="permissions.can_rate && !customerProfile"
          type="button"
          class="rt-btn-primary"
          @click="openRatingForm"
        >
          Ocenite
        </button>
      </div>
    </section>

    <!-- filteri -->
    <section class="rt-card rt-filters" aria-labelledby="ratings-filters-title">
      <div class="rt-filters-head">
        <h2 id="ratings-filters-title" class="rt-card-title">Filteri</h2>
        <span class="rt-hint">Klikni na filter da vidiš samo te ocene.</span>
      </div>

      <div class="rt-filter-label">Uloga</div>
      <div class="rt-pills" role="group" aria-label="Uloga">
        <button
          v-for="tab in tabOptions"
          :key="tab.value"
          type="button"
          class="rt-pill"
          :class="{ on: selectedTab === tab.value }"
          :aria-pressed="selectedTab === tab.value"
          @click="emit('change-tab', tab.value)"
        >{{ tab.text }}</button>
      </div>

      <div class="rt-filter-label">Vrsta ocene</div>
      <div class="rt-pills" role="group" aria-label="Vrsta ocene">
        <button
          v-for="option in rateOptions"
          :key="option.value"
          type="button"
          class="rt-pill"
          :class="{ on: selectedRate === option.value, neg: option.value === 'negativno' }"
          :aria-pressed="selectedRate === option.value"
          @click="emit('change-rate', option.value)"
        >{{ option.text }}</button>
      </div>
    </section>

    <!-- forma za ocenu (javna stranica) -->
    <div v-if="showRatingForm == true" class="rt-card ratings-form-card">

      <BRow>
        <BCol lg="8">
          <div class="ratings-form-group">
            <label class="ratings-form-label">Ocena se odnosi na oglas:</label>

            <BFormSelect
              v-model="form.ad_conversation_id"
              :options="rateableAds"
            />
          </div>

          <div class="ratings-form-group mt-4">
            <div class="ratings-question-title">
              1. Da li je saradnja ispoštovana?
            </div>

            <div class="ratings-radio-row">
              <label class="ratings-radio-option">
                <input
                  v-model="form.cooperation_rating"
                  type="radio"
                  value="yes"
                >
                <span>Da</span>
              </label>

              <label class="ratings-radio-option">
                <input
                  v-model="form.cooperation_rating"
                  type="radio"
                  value="partial"
                >
                <span>Delimično</span>
              </label>

              <label class="ratings-radio-option">
                <input
                  v-model="form.cooperation_rating"
                  type="radio"
                  value="no"
                >
                <span>Ne</span>
              </label>
            </div>
          </div>

          <div class="ratings-form-group mt-4">
            <label class="ratings-form-label">2. Komentar uz ocenu:</label>

            <textarea
              v-model="form.comment"
              class="form-control ratings-textarea"
              rows="5"
              placeholder="Unesite kratak komentar ovde"
           ></textarea>
          </div>
         <BRow>
        <BCol>
          <BAlert variant="danger"
                  :model-value="errorMessage != ''"
                  class="mb-3">
              {{ errorMessage }}
          </BAlert>

             <BAlert  variant="success"
                     :model-value="successMessage != ''"
                     class="mb-3">
              {{ successMessage }}
            </BAlert>
         </BCol>
      </BRow>
          <div class="ratings-form-actions">
            <button
              type="button"
              class="rt-btn rt-btn-quiet"
              @click="closeRatingForm"
            >
              Odustanite
            </button>

            <button
              type="button"
              class="rt-btn-primary"
              :disabled="localLoading"
              @click="submitRating"
            >
              Ocenite
            </button>
          </div>
        </BCol>
      </BRow>
    </div>

    <!-- poruka o uspehu/grešci kad forma nije otvorena (odgovor, izmena) -->
    <BAlert
      v-if="!showRatingForm && (errorMessage || successMessage)"
      :variant="errorMessage ? 'danger' : 'success'"
      :model-value="true"
      class="mb-3"
    >
      {{ errorMessage || successMessage }}
    </BAlert>

    <!-- ocene -->
    <div v-if="ratings.length">
      <article
        v-for="(item, index) in ratings"
        :key="item.id"
        class="rt-item"
        :class="item.rating === 'positive' ? 'pos' : 'neg'"
      >
        <header class="rt-item-head">
          <span class="rt-av sm" aria-hidden="true">{{ initials(item.from_customer?.name) }}</span>

          <div class="rt-item-who">
            <div class="rt-item-name">{{ item.from_customer?.name || 'Nepoznat korisnik' }}</div>
            <div class="rt-item-date">{{ formatDate(item.rated_at) }}</div>
          </div>

          <span class="rt-tag" :class="item.rating === 'positive' ? 'p' : 'n'">
            {{ item.rating === 'positive' ? 'Pozitivna' : 'Negativna' }}
          </span>
        </header>

        <p class="rt-item-ad">
          Oglas: <strong>{{ item.advertisement?.title || 'Oglas' }}</strong>
          <span class="rt-chip">Saradnja ispoštovana: {{ cooperationLabel(item.cooperation_rating) }}</span>
        </p>

        <div
          v-if="item.comment"
          class="rt-comment"
        >
          {{ item.comment }}
        </div>

        <div class="rt-actions">
          <button
            v-if="item.can_reply && !item.reply_comment"
            type="button"
            class="rt-btn rt-btn-sm"
            @click="openReplyForm(item)"
          >
            Odgovorite
          </button>

          <button
            v-if="item.comment && item.can_edit_comment"
            type="button"
            class="rt-btn rt-btn-sm rt-btn-grey"
            @click="openEditCommentForm(item)"
          >
            Izmeni komentar
          </button>

          <button
            v-if="item.reply_comment && item.can_edit_reply"
            type="button"
            class="rt-btn rt-btn-sm rt-btn-grey"
            @click="openEditReplyForm(item)"
          >
            Izmeni odgovor
          </button>
        </div>

        <div
          v-if="activeReplyId === item.id"
          class="rt-editor"
        >
          <textarea
            v-model="replyForm.reply_comment"
            class="form-control"
            rows="4"
            placeholder="Unesite odgovor"
          ></textarea>

          <div class="rt-editor-actions">
            <button
              type="button"
              class="rt-btn rt-btn-quiet"
              @click="cancelReplyForm"
            >
              Odustanite
            </button>

            <button
              type="button"
              class="rt-btn-primary"
              :disabled="localLoading"
              @click="submitReply"
            >
              Sačuvaj odgovor
            </button>
          </div>
        </div>

        <div
          v-if="activeEditCommentId === item.id"
          class="rt-editor"
        >
          <textarea
            v-model="editCommentForm.comment"
            class="form-control"
            rows="4"
            placeholder="Izmenite komentar"
          ></textarea>

          <div class="rt-editor-actions">
            <button
              type="button"
              class="rt-btn rt-btn-quiet"
              @click="cancelEditCommentForm"
            >
              Odustanite
            </button>

            <button
              type="button"
              class="rt-btn-primary"
              :disabled="localLoading"
              @click="submitEditComment"
            >
              Sačuvaj izmenu
            </button>
          </div>
        </div>

        <div
          v-if="activeEditReplyId === item.id"
          class="rt-editor"
        >
          <textarea
            v-model="editReplyForm.reply_comment"
            class="form-control"
            rows="4"
            placeholder="Izmenite odgovor"
          ></textarea>

          <div class="rt-editor-actions">
            <button
              type="button"
              class="rt-btn rt-btn-quiet"
              @click="cancelEditReplyForm"
            >
              Odustanite
            </button>

            <button
              type="button"
              class="rt-btn-primary"
              :disabled="localLoading"
              @click="submitEditReply"
            >
              Sačuvaj izmenu
            </button>
          </div>
        </div>

        <div
          v-if="item.reply_comment"
          class="rt-reply"
        >
          <div class="rt-reply-head">
            <span class="rt-reply-label">Odgovor</span>

            <span
              v-if="item.reply_at"
              class="rt-reply-date"
            >
              {{ formatDate(item.reply_at) }}
            </span>
          </div>

          <div class="rt-reply-text">
            {{ item.reply_comment }}
          </div>
        </div>

        <div
          v-if="!customerProfile && $screen.showMobileAds && (index + 1) % 3 === 0"
          class="a-container mt-3 mb-3"
        >
          <AdsSingleMobile></AdsSingleMobile>
        </div>
      </article>

      <div class="a-container mt-3 mb-3" v-if="!customerProfile && $screen.isProduction">
        <AdsSingleCenter></AdsSingleCenter>
      </div>

      <div
        v-if="pagination.total > pagination.per_page"
        class="d-flex justify-content-center mt-4"
      >
        <BPagination
          :model-value="pagination.current_page"
          :total-rows="pagination.total"
          :per-page="pagination.per_page"
          @update:model-value="emit('change-page', $event)"
        />
      </div>
    </div>

    <div
      v-else-if="!loading"
      class="rt-empty"
    >
      Trenutno nema ocena za prikaz.
    </div>
  </BContainer>
</template>

<script setup>

import {
  BContainer,
  BRow,
  BCol,
  BFormSelect,
  BAlert,
  BPagination
} from 'bootstrap-vue-next'

const AdsSingleMobile = defineAsyncComponent(() =>
  import('~/components/GoogleAdvertisements/AdsSingleMobile.vue')
)

const AdsSingleCenter = defineAsyncComponent(() =>
  import('~/components/GoogleAdvertisements/AdsSingleCenter.vue')
)


const props = defineProps({
  customer: {
    type: Object,
    default: () => ({}),
     required: false
  },
  summary: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: () => ({})
  },
  ratings: {
    type: Array,
    default: () => []
  },
  pagination: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  },
  selectedTab: {
    type: String,
    default: 'all'
  },
  selectedRate: {
    type: String,
    default: 'sve-ocene'
  },
  rateOptions: {
    type: Array,
    default: () => []
  },
  customerId: {
    type: Number,
    required: false
  }
  ,
  customerProfile: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'change-tab',
  'change-rate',
  'change-page',
  'refresh-data'
])

const { $apiAuth } = useNuxtApp()

const localLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const showRatingForm = ref(false)

// uloga ocenjenog korisnika (vrednosti koje stranice već koriste za tab)
const tabOptions = [
  { value: 'all', text: 'Sve ocene' },
  { value: 'seller', text: 'Kao prodavac' },
  { value: 'buyer', text: 'Kao kupac' }
]

// naslov: na profilu "Moje ocene", na javnoj stranici ime korisnika (H1 za pretraživače)
const pageTitle = computed(() =>
  props.customerProfile ? 'Moje ocene' : `Ocene korisnika: ${props.customer?.customer_name ?? ''}`
)

const positiveCount = computed(() => Number(props.summary?.positive_count ?? 0))
const negativeCount = computed(() => Number(props.summary?.negative_count ?? 0))
const totalRatings = computed(() => positiveCount.value + negativeCount.value)

const positivePercent = computed(() =>
  totalRatings.value > 0 ? Math.round((positiveCount.value / totalRatings.value) * 100) : 0
)

// inicijali za krug pored imena (najviše dva slova)
const initials = (name) => {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean)

  if (!parts.length) return '?'

  return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('')
}

const rateableAds = ref([
  { value: null, text: 'Izaberite oglas' }
])

const conversations = ref([])

const form = reactive({
  ad_conversation_id: null,
  cooperation_rating: '',
  comment: ''
})

const replyForm = reactive({
  rating_id: null,
  reply_comment: ''
})

const editCommentForm = reactive({
  rating_id: null,
  comment: ''
})

const editReplyForm = reactive({
  rating_id: null,
  reply_comment: ''
})

const activeReplyId = ref(null)
const activeEditCommentId = ref(null)
const activeEditReplyId = ref(null)

const cooperationLabel = (value) => {
  if (value === 'yes') return 'Da'
  if (value === 'partial') return 'Delimično'
  if (value === 'no') return 'Ne'
  return '-'
}

const formatDate = (value) => {
  if (!value) return ''

  // vec formatiran datum (dd.mm.gggg.) se ne tumaci ponovo, da se dan i mesec ne zamene
  if (/^\d{1,2}\.\d{1,2}\.\d{4}\.?$/.test(String(value).trim())) return String(value).trim()

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date)
}

const resetInlineForms = () => {
  activeReplyId.value = null
  activeEditCommentId.value = null
  activeEditReplyId.value = null

  replyForm.rating_id = null
  replyForm.reply_comment = ''

  editCommentForm.rating_id = null
  editCommentForm.comment = ''

  editReplyForm.rating_id = null
  editReplyForm.reply_comment = ''
}

const openRatingForm = async () => {


  errorMessage.value = ''
  successMessage.value = ''

  await fetchConversations()

  showRatingForm.value = true
}

const closeRatingForm = () => {
  showRatingForm.value = false
  form.ad_conversation_id = null
  form.cooperation_rating = ''
  form.comment = ''
}

const openReplyForm = (item) => {
  activeReplyId.value = item.id
  activeEditCommentId.value = null
  activeEditReplyId.value = null

  replyForm.rating_id = item.id
  replyForm.reply_comment = item.reply_comment || ''

  errorMessage.value = ''
  successMessage.value = ''
}

const cancelReplyForm = () => {
  activeReplyId.value = null
  replyForm.rating_id = null
  replyForm.reply_comment = ''
}

const openEditCommentForm = (item) => {
  activeEditCommentId.value = item.id
  activeReplyId.value = null
  activeEditReplyId.value = null

  editCommentForm.rating_id = item.id
  editCommentForm.comment = item.comment || ''

  errorMessage.value = ''
  successMessage.value = ''
}

const cancelEditCommentForm = () => {
  activeEditCommentId.value = null
  editCommentForm.rating_id = null
  editCommentForm.comment = ''
}

const openEditReplyForm = (item) => {
  activeEditReplyId.value = item.id
  activeReplyId.value = null
  activeEditCommentId.value = null

  editReplyForm.rating_id = item.id
  editReplyForm.reply_comment = item.reply_comment || ''

  errorMessage.value = ''
  successMessage.value = ''
}

const cancelEditReplyForm = () => {
  activeEditReplyId.value = null
  editReplyForm.rating_id = null
  editReplyForm.reply_comment = ''
}

const fetchConversations = async () => {
  try {
    const { data } = await $apiAuth(
      `/api/ad/conversations/${props.customerId}/rateable`,
      {
        method: 'GET',
        use: 'customer'
      }
    )

    conversations.value = data ?? []

    if (Array.isArray(conversations.value)) {
      rateableAds.value = [
        { value: null, text: 'Izaberite oglas' },
        ...conversations.value.map(item => ({
          value: item.ad_conversation_id ?? item.id,
          text: item.title ?? `Oglas #${item.id}`
        }))
      ]
    }
  } catch (error) {
    
  }
}

const submitRating = async () => {
  const Swal = (await import('sweetalert2')).default

  errorMessage.value = ''
  successMessage.value = ''

  if (!form.ad_conversation_id) {
    errorMessage.value = 'Potrebno je da izaberete oglas.'
    return
  }

  if (!form.cooperation_rating) {
    errorMessage.value = 'Potrebno je da izaberete ocenu saradnje.'
    return
  }

  try {
    localLoading.value = true

    const ratingValue = form.cooperation_rating === 'no'
      ? 'negative'
      : 'positive'

    await $apiAuth(`/api/ad/conversations/${form.ad_conversation_id}/ratings`, {
      method: 'POST',
      use: 'customer',
      body: {
        rating: ratingValue,
        cooperation_rating: form.cooperation_rating,
        comment: form.comment?.trim() || null
      }
    })

    successMessage.value = 'Ocena je uspešno sačuvana.'
    closeRatingForm()
    emit('refresh-data')
  } catch (error) {


    errorMessage.value = error?.data?.message || 'Došlo je do greške prilikom slanja ocene.'
  } finally {
    localLoading.value = false
  }
}

const submitReply = async () => {
  errorMessage.value = ''
  successMessage.value = ''

  if (!replyForm.rating_id || !replyForm.reply_comment?.trim()) {
    errorMessage.value = 'Unesite odgovor.'
    return
  }

  try {
    localLoading.value = true

    await $apiAuth(`/api/ad/ratings/${replyForm.rating_id}/reply`, {
      method: 'PATCH',
      use: 'customer',
      body: {
        reply_comment: replyForm.reply_comment.trim()
      }
    })

    successMessage.value = 'Odgovor je uspešno sačuvan.'
    cancelReplyForm()
    emit('refresh-data')
  } catch (error) {
    errorMessage.value = error?.data?.message || 'Greška prilikom čuvanja odgovora.'
  } finally {
    localLoading.value = false
  }
}

const submitEditComment = async () => {
  errorMessage.value = ''
  successMessage.value = ''

  if (!editCommentForm.rating_id || !editCommentForm.comment?.trim()) {
    errorMessage.value = 'Unesite komentar.'
    return
  }

  try {
    localLoading.value = true

    await $apiAuth(`/api/ad/ratings/${editCommentForm.rating_id}/comment`, {
      method: 'PATCH',
      use: 'customer',
      body: {
        comment: editCommentForm.comment.trim()
      }
    })

    successMessage.value = 'Komentar je uspešno izmenjen.'
    cancelEditCommentForm()
    emit('refresh-data')
  } catch (error) {
    errorMessage.value = error?.data?.message || 'Greška prilikom izmene komentara.'
  } finally {
    localLoading.value = false
  }
}

const submitEditReply = async () => {
  errorMessage.value = ''
  successMessage.value = ''

  if (!editReplyForm.rating_id || !editReplyForm.reply_comment?.trim()) {
    errorMessage.value = 'Unesite odgovor.'
    return
  }

  try {
    localLoading.value = true

    await $apiAuth(`/api/ad/ratings/${editReplyForm.rating_id}/reply-comment`, {
      method: 'PATCH',
      use: 'customer',
      body: {
        reply_comment: editReplyForm.reply_comment.trim()
      }
    })

    successMessage.value = 'Odgovor je uspešno izmenjen.'
    cancelEditReplyForm()
    emit('refresh-data')
  } catch (error) {
    errorMessage.value = error?.data?.message || 'Greška prilikom izmene odgovora.'
  } finally {
    localLoading.value = false
  }
}

</script>
